import {CommentsCategory} from '@domain/comments'
import {getPermissionsByUserId, ApiClient} from '@hconnect/apiclient'
import {
  Status,
  Material,
  Recipe,
  AttachmentFile,
  MaterialStoragesHistory,
  MaterialStorage,
  MaterialsHistory,
  StockLevelOverwrite,
  AssetsHistory
} from '@hconnect/common/types'
import {FetchQueryOptions} from '@tanstack/react-query'
import type {AxiosRequestHeaders} from 'axios'
import chunk from 'lodash/chunk'
import readExcel from 'read-excel-file'

import {PlannerQueryClient} from '../components/providers/QueryProvider'
import type {
  Schedule,
  Comment,
  ElectricityResponse,
  CostAvoidanceMonthly,
  ScheduleCostAvoidance,
  CostAvoidanceForRange,
  MaterialOrder,
  ScheduleAgreementsByVendor,
  StockDevelopmentResponse,
  LatestPeakLoadWindow,
  ElectricityPrice,
  GroupedUnplannedDelivery,
  StockReachResponse,
  OptimizedSchedule,
  OptimizerRunResponse,
  OptimizedSchedulesStats,
  OptimizerConstraintsDTO
} from '../interfaces/api'
import {PlantConfigData} from '../interfaces/api'
import {AutofillStatusDataAPIResponse} from '../interfaces/api/autofill'
import {AssetCapacityResponse} from '../interfaces/api/kpi'
import {MaterialDemandByDay} from '../interfaces/api/materialDemand'
import {OptimizerSettingsDTO} from '../interfaces/api/optimizer'
import {PermissionParsed} from '../interfaces/api/permissions'
import {validateBaseLoadInAssets} from '../selectors/assets'

import {dsApiBasePath} from './apiClient'
import {Queries, QueryReturn} from './queryType'

export const fetchHcemQuery = <K extends keyof Queries, T extends Queries>(
  queryKey: K,
  params: Parameters<T[K]>[0]['params'],
  options?: Omit<FetchQueryOptions<QueryReturn<K>, Error, QueryReturn<K>>, 'queryKey' | 'queryFn'>
) =>
  PlannerQueryClient.fetchQuery<QueryReturn<K>>({
    queryKey: [queryKey, ...params],
    queryFn: async ({signal}) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return queries[queryKey]({signal, params}) as Promise<QueryReturn<K>>
    },
    ...options
  })

/**
 * we should use it for prefetchin because itoesn't throw to the error boundaries
 */
export const prefetchPlannerQuery = <K extends keyof Queries, TReturn = QueryReturn<K>>(
  queryKey: K,
  params: Parameters<Queries[K]>[0]['params'],
  options?: Omit<FetchQueryOptions<QueryReturn<K>, unknown, TReturn>, 'queryKey' | 'queryFn'>
) => {
  void PlannerQueryClient.prefetchQuery({
    queryKey: [queryKey, ...params],
    queryFn: async ({signal}) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return queries[queryKey]({signal, params}) as Promise<QueryReturn<K>>
    },
    ...options
  })
}

export const queries: Queries = {
  permissions: async ({params: [userId]}) => {
    const permissions = await getPermissionsByUserId(ApiClient.axiosInstance)(userId, undefined)
    const permissionsParsed: PermissionParsed[] = permissions.map((permission) => ({
      ...permission,
      parsedDataScope: JSON.parse(permission.dataScope) as PermissionParsed['parsedDataScope']
    }))
    return permissionsParsed
  },
  plantConfig: async ({params: [plantCode]}) => {
    const path = `${dsApiBasePath}/v2/config/${plantCode}`
    const response = await ApiClient.axiosInstance.get<PlantConfigData>(path)
    return response.data
  },
  autofillStatus: async ({params: [plantCode]}) => {
    const path = `${dsApiBasePath}/v2/autofill/${plantCode}/status`
    const response = await ApiClient.axiosInstance.get<AutofillStatusDataAPIResponse>(path)
    return {
      status: response.data.status,
      lastUpdatedAt: response.data.last_updated_at
    }
  },
  peakLoadWindows: async ({params: [{plantCode, from, to}]}) => {
    const path = `${dsApiBasePath}/peak_load_windows/${plantCode}`
    const params = {time_from: from, time_to: to}
    const response = await ApiClient.axiosInstance.get<LatestPeakLoadWindow[]>(path, {
      params
    })
    return response.data
  },
  electricityPrice: async ({params: [{plantCode, from, to}]}) => {
    const path = `${dsApiBasePath}/electricity/${plantCode}`
    const params = {time_from: from, time_to: to}
    const response = await ApiClient.axiosInstance.get<ElectricityPrice>(path, {params})
    return response.data
  },
  // C# Endpoints
  downloadElectricityDataExcelFile: async ({params: [plantCode, createdByOptimiser]}) => {
    const path = '/electricity'
    const params: Record<string, unknown> = {
      plantCode,
      ...(createdByOptimiser && {createdByOptimiser})
    }
    const {data, headers} = await ApiClient.axiosInstance.get<Blob>(path, {
      params,
      responseType: 'blob',
      transformRequest: (data, headers) => {
        ;(headers as AxiosRequestHeaders)['Accept'] =
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        return data
      }
    })
    return [data, headers]
  },
  schedule: async ({params: [{plantCode, start, end}]}) => {
    const path = '/schedules/current'
    const response = await ApiClient.axiosInstance.get<Schedule>(path, {
      params: {plantCode, start, end}
    })
    return response.data
  },
  optimizedSchedulesStats: async ({params: [{plantCode}]}) => {
    const path = `/plants/${plantCode}/schedules/optimized/stats`
    const response = await ApiClient.axiosInstance.get<OptimizedSchedulesStats>(path)
    return response.data
  },
  optimizerRunsAndSchedules: async ({params: [{plantCode}]}) => {
    const optimizerRuns = ApiClient.axiosInstance.get<OptimizerRunResponse[]>(
      `${dsApiBasePath}/v2/optimizer/${plantCode}/runs`
    )
    const optimizedSchedules = ApiClient.axiosInstance.get<OptimizedSchedule[]>(
      `/plants/${plantCode}/schedules/optimized`
    )
    const [otpimizerRunsResponse, optimizedSchedulesResponse] = await Promise.all([
      optimizerRuns,
      optimizedSchedules
    ])

    return {
      optimizerRuns: otpimizerRunsResponse.data,
      optimizedSchedules: optimizedSchedulesResponse.data
    }
  },
  electricity: async ({params: [{plantCode, start, end}]}) => {
    const path = '/schedules/current/load-curves'
    const params: Record<string, unknown> = {
      plantCode,
      start,
      end
    }
    const {data} = await ApiClient.axiosInstance.get<ElectricityResponse>(path, {params})
    return data
  },
  comments: async ({params: [{plantCode, commentsCategory, commentsCategoryInstanceIds}]}) => {
    const path = `/comments/${plantCode}/${commentsCategory}`
    if (!commentsCategoryInstanceIds) {
      return Promise.reject(
        `comment category instance ids for category ${commentsCategory} were not provided`
      )
    }
    // HOTFIX: BE has a limit on url length, so we need to chunk the uuids for schedule items
    const AssetOperationModeIdsLimit = 50
    if (
      commentsCategory === CommentsCategory.AssetOperationTimes &&
      commentsCategoryInstanceIds.length > AssetOperationModeIdsLimit
    ) {
      const chunkedIds = chunk(commentsCategoryInstanceIds, AssetOperationModeIdsLimit)
      const comments = await Promise.all(
        chunkedIds.map((ids) =>
          ApiClient.axiosInstance.get<Comment[]>(path, {
            params: {commentCategoryInstanceIds: ids.join(',')}
          })
        )
      )
      return comments.flatMap((response) => response.data)
    }
    const params: Record<string, unknown> = {
      commentCategoryInstanceIds: commentsCategoryInstanceIds.join(',')
    }
    const response = await ApiClient.axiosInstance.get<Comment[]>(path, {params})
    return response.data
  },
  materials: async ({params: [plantCode]}) => {
    const path = `/plants/${plantCode}/materials`
    const response = await ApiClient.axiosInstance.get<Material[]>(path)
    return response.data
  },
  materialsHistory: async ({params: [plantCode, from, to]}) => {
    const path = `/plants/${plantCode}/materials/history`
    const params = {from, to}
    const response = await ApiClient.axiosInstance.get<MaterialsHistory>(path, {params})
    return response.data
  },
  materialsRecipes: async ({params: [{plantCode, assetType}]}) => {
    const path = `/plants/${plantCode}/materials/recipes`
    const params = assetType ? {assetType} : undefined
    const response = await ApiClient.axiosInstance.get<Recipe[]>(path, {params})
    return response.data
  },
  assetsHistory: async ({params: [plantCode, from, to]}) => {
    const path = `/plants/${plantCode}/assets/history`
    const params = {from, to}
    const {data: assetsHistory} = await ApiClient.axiosInstance.get<AssetsHistory>(path, {
      params
    })
    const latestAssets = Object.values(assetsHistory)
      .map((assets) => assets[assets.length - 1])
      .filter((asset) => asset.status !== Status.Deleted)
    // validating that base load is present in latest assets
    validateBaseLoadInAssets(latestAssets)
    return assetsHistory
  },
  materialStorageHistory: async ({params: [plantCode, from, to]}) => {
    const path = `/plants/${plantCode}/material-storage/history`
    const params = {from, to}
    const response = await ApiClient.axiosInstance.get<MaterialStoragesHistory>(path, {
      params
    })
    return response.data
  },
  materialStorage: async ({params: [plantCode]}): Promise<MaterialStorage[]> => {
    const path = `/plants/${plantCode}/material-storage`
    const response = await ApiClient.axiosInstance.get<MaterialStorage[]>(path)
    return response.data
  },
  costAvoidanceMonthly: async ({params: [{plantCode, fromYearMonth, toYearMonth}]}) => {
    const path = `/plants/${plantCode}/schedules/kpi/cost-avoidance/monthly`
    // params should be iso date, but only year and month will be taken into account by BE
    const params = {from: fromYearMonth, to: toYearMonth}
    const response = await ApiClient.axiosInstance.get<CostAvoidanceMonthly[]>(path, {
      params
    })
    return response.data
  },
  costAvoidanceDaily: async ({params: [{plantCode, fromDay, toDay}]}) => {
    const path = `/plants/${plantCode}/schedules/kpi/cost-avoidance`
    // params should be iso date, but only year and month will be taken into account by BE
    const params = {from: fromDay, to: toDay}
    const response = await ApiClient.axiosInstance.get<ScheduleCostAvoidance[]>(path, {
      params
    })
    return response.data
  },
  scheduleCostAvoidance: async ({params: [{plantCode, scheduleId}]}) => {
    const path = `/plants/${plantCode}/schedules/${scheduleId}/kpi/cost-avoidance`
    const response = await ApiClient.axiosInstance.get<ScheduleCostAvoidance>(path)
    return response.data
  },
  costAvoidanceForRange: async ({params: [{plantCode, scheduleId, from, to}]}) => {
    const path = `/plants/${plantCode}/schedules/${scheduleId}/kpi/cost-avoidance-for-range`
    // params should be iso date
    const params = {from, to}
    const response = await ApiClient.axiosInstance.get<CostAvoidanceForRange>(path, {
      params
    })
    return response.data
  },
  stockOverwrites: async ({params: [{plantCode, materialId, from, to}]}) => {
    const path = `/plants/${plantCode}/material-storage/stock-level/material/${materialId}`
    // params should be iso date
    const params = {from, to}
    const response = await ApiClient.axiosInstance.get<StockLevelOverwrite[]>(path, {
      params
    })
    return response.data
  },
  materialOrders: async ({params: [{plantCode, from, to, materialId}]}) => {
    const path = `/plants/${plantCode}/material-orders`
    const params = {from, to, materialId}
    const response = await ApiClient.axiosInstance.get<MaterialOrder[]>(path, {params})
    return response.data
  },
  materialUnplannedDeliveries: async ({params: [{plantCode, from, to, materialId}]}) => {
    const path = `/plants/${plantCode}/material-orders/daily-unplanned-deliveries`
    const params = {from, to, materialId}
    const response = await ApiClient.axiosInstance.get<GroupedUnplannedDelivery[]>(path, {
      params
    })
    return response.data
  },
  scheduleAgreementsByVendor: async ({params: [{plantCode, materialId}]}) => {
    const path = `/plants/${plantCode}/materials/${materialId}/schedule-agreements`
    const response = await ApiClient.axiosInstance.get<ScheduleAgreementsByVendor[]>(path)
    return response.data
  },
  attachmentFile: async ({params: [{attachment, isPreview}]}) => {
    const {id, fileName, url, mediaType, previewUrl} = attachment
    const fileUrl = isPreview && previewUrl ? previewUrl : url
    const path = `/documents/${fileUrl}`
    const response = await ApiClient.axiosInstance.get<ArrayBuffer>(path, {
      responseType: 'arraybuffer',
      headers: {
        'Content-Type': mediaType,
        Accept: mediaType
      }
    })
    const blobUrl = URL.createObjectURL(new Blob([response.data]))
    return {
      id,
      name: fileName,
      contentType: mediaType,
      preview: blobUrl,
      isImage: mediaType.includes('image')
    }
  },
  attachmentFiles: async ({params: [{attachments, isPreview}]}) => {
    const attachmentQueries = attachments.map((attachment) =>
      fetchHcemQuery('attachmentFile', [{attachment, isPreview}])
    )
    const data: AttachmentFile[] | undefined = await Promise.all(attachmentQueries)
    return data ?? []
  },
  stockDevelopment: async ({
    params: [{plantCode, materialIds, from, to, useOptimizedSchedule, scheduleId}]
  }) => {
    if (!from || !to) {
      return undefined
    }
    const path = `plants/${plantCode}/schedules/kpi/stock-development`
    const params = {
      from,
      to,
      materialIds: materialIds.join(','),
      useOptimizedSchedule,
      scheduleId
    }
    const response = await ApiClient.axiosInstance.get<StockDevelopmentResponse>(path, {
      params
    })
    return response.data
  },
  stockReach: async ({params: [{plantCode, materialIds, to}]}) => {
    const path = `/plants/${plantCode}/schedules/kpi/stock-reach`
    const params = {
      materialIds: materialIds.join(','),
      to
    }
    const response = await ApiClient.axiosInstance.get<StockReachResponse>(path, {params})
    return response.data
  },
  assetCapacity: async ({params: [plantCode, {from, to}]}) => {
    const path = `/plants/${plantCode}/schedules/kpi/asset-capacity`
    const params = {from, to}
    const response = await ApiClient.axiosInstance.get<AssetCapacityResponse>(path, {
      params
    })
    return response.data
  },
  materialDemand: async ({params: [plantCode, {fromDate, toDate}]}) => {
    const path = `/plants/${plantCode}/material-demand/daily`
    const params = {from: fromDate, to: toDate}
    const response = await ApiClient.axiosInstance.get<MaterialDemandByDay>(path, {params})
    return response.data
  },
  downloadSalesForecastExcelTemplate: async ({params: [plantCode]}) => {
    const path = `/plants/${plantCode}/material-demand/sales-forecast/template`
    const {data, headers} = await ApiClient.axiosInstance.get<Blob>(path, {
      responseType: 'blob',
      transformRequest: (data, headers) => {
        ;(headers as AxiosRequestHeaders)['Accept'] =
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        return data
      }
    })
    return [data, headers]
  },
  readSalesForecastExcel: async ({params: [file]}) => {
    if (!file.originalFile) {
      return undefined
    }
    const rows = await readExcel(file.originalFile)
    // removing info rows before column row
    const columnRowIdex = 5
    rows.splice(0, columnRowIdex)
    return rows
  },
  optimizerSettings: async ({params: [plantCode]}) => {
    const path = `${dsApiBasePath}/v2/optimizer/${plantCode}/settings`
    const response = await ApiClient.axiosInstance.get<OptimizerSettingsDTO>(path)
    return response.data
  },
  optimizerConstraints: async ({params: [plantCode]}) => {
    const path = `${dsApiBasePath}/v2/optimizer/${plantCode}/constraints`
    const response = await ApiClient.axiosInstance.get<OptimizerConstraintsDTO>(path)
    return response.data
  }
}
